import { useCallback, useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import {
  Constants,
  LOB,
  ProviderType,
  ProviderTypes,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { getFeatureFlag } from '../../../../common/Utils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useMinProviderCount } from '../../../../hooks/useMinProviderCount';
import { useProviderResultsV2 } from '../../../../hooks/useProviderSearch';
import { useProviderSearchTelemetry } from '../../../../hooks/useProviderSearchTelemetry';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import { useSuppressNiceMembers } from '../../../../hooks/useSuppressNiceMembers';
import { useTier1PlanIdentifier } from '../../../../hooks/useTier1PlanIdentifier';
import {
  getProviderSearchParams,
  getRecommendationPayload,
} from '../../../../utils/providerSearch.utils';
import {
  getCurrentMember,
  getProviderType,
} from '../../../../utils/user.utils';
import { concatenateStrings, getTelemetryCoverageType } from '../MapView/utils';
import { checkError, formatData, getDataForMixedResults } from './searchUtils';

type GetProviderSearchResultsProps = {
  setShowFacilities: (x: boolean) => any;
  setShowProviderGroups: (x: boolean) => any;
  setShowProviders: (x: boolean) => any;
  providerSearchParams: any;
  choosePCP?: boolean;
  cleanUpProviderCalls: boolean;
  pseudoVerticalsDetails: any;
  selectedCategory: any;
  selectedCategoryPayload: any;
  specialtySearch: boolean;
  searchType: string;
  providerQueryTypes: any[];
  enableCategoryCountV2: boolean;
  providerType?: ProviderType | 'All';
  setShowVirtualResultsOnly: (x: boolean) => any;
};

export const useSearchResultsProviderResults = (
  customParams: any,
  onProviderResultsLoaded?: (providersResult: any) => void,
  onOrgResultsLoaded?: (organizationResult: any) => void,
  onMedGrpResultsLoaded?: (medGrpResult: any) => void,
  categoryCountResults?: any,
  chipValue?: string
) => {
  const { longitude, latitude, name } = getGeoLocationFromStorage();

  let skipCalls = false;
  const allResults = [
    ...(categoryCountResults?.nameResults || []),
    ...(categoryCountResults?.specialityResults || []),
  ];
  // check if the chipValue exists in the category results with pseudoSearchOnly true and skip calls if it does
  const chipId = Constants.MOCK_CARE_CATEGORIES.find(
    (c) => c.value === chipValue
  )?.id;
  if (
    allResults.length &&
    allResults.find(
      (item) => item.chipCategory === chipId && item.pseudoSearchOnly
    )
  ) {
    skipCalls = true;
  }
  const isSmartChoiceEnabled = useSmartChoice();
  const recommendationPreferences = getRecommendationPayload(
    customParams?.combinedIncludeSpecialityRollupCodes
  );

  let updatedCustomParams = {
    ...customParams,
  };
  if (isSmartChoiceEnabled) {
    updatedCustomParams = {
      ...updatedCustomParams,
      recommendationPreferences,
    };
  }

  const [codes, setCodes] = useState<string[]>([]);
  const [completedCodes, setCompletedCodes] = useState<string[]>([]);
  const [, providerSearchTelemetry] = useProviderSearchTelemetry({});
  type ProviderSearchParamsState =
    | {
        search?: string;
        searchType?: string;
        searchRadius?: number;
        coverageType?: string | string[];
        [key: string]: any;
      }
    | undefined;
  const [providerSearchParams, setProviderSearchParams] =
    useState<ProviderSearchParamsState>();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const isSearchRelevancyEventTrackingEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD
  );
  const [providersResult, getProvidersResult] = useProviderResultsV2(
    {
      ...updatedCustomParams,
      minimumProvidersReturn: useMinProviderCount(ProviderTypes.PRACTITIONER),
    },
    {
      onCompleted: (providersResult) => {
        onProviderResultsLoaded?.(providersResult);
        setCompletedCodes((prev) => [...prev, 'P']);
      },
    }
  );
  const [organizationResult, getOrgResult] = useProviderResultsV2(
    {
      ...updatedCustomParams,
      minimumProvidersReturn: useMinProviderCount(ProviderTypes.ORGANIZATION),
    },
    {
      onCompleted: (organizationResult) => {
        onOrgResultsLoaded?.(organizationResult);
        setCompletedCodes((prev) => [...prev, 'O']);
      },
    }
  );
  const [medGrpResult, getMedGroupResult] = useProviderResultsV2(
    {
      ...updatedCustomParams,
      minimumProvidersReturn: useMinProviderCount(ProviderTypes.MEDICAL_GROUP),
    },
    {
      onCompleted: (medGrpResult) => {
        onMedGrpResultsLoaded?.(medGrpResult);
        setCompletedCodes((prev) => [...prev, 'M']);
      },
    }
  );
  const currentMember = getCurrentMember();

  const {
    data: practitionerData,
    isLoading: providerIsLoading,
    error: providerHasError,
  } = providersResult;

  const {
    data: orgdata,
    isLoading: orgIsLoading,
    error: orgHasError,
  } = organizationResult;

  const {
    data: medGrpData,
    isLoading: medGrpIsLoading,
    error: medGrpHasError,
  } = medGrpResult;

  const data: any = formatData(practitionerData, orgdata, medGrpData);

  const isTiredPlanVal = data?.isTiredPlan ?? undefined;

  const error: boolean =
    !medGrpIsLoading &&
    !orgHasError &&
    !providerHasError &&
    checkError(providerHasError, orgHasError, medGrpHasError);

  if (isTiredPlanVal !== undefined) {
    useTier1PlanIdentifier(isTiredPlanVal);
  }
  const [enableCategoryCountV2] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CATEGORY_COUNT_V2,
  ]);

  const { suppressForUspNice: suppressProviderGroupForPCP } =
    useSuppressNiceMembers();

  const getProviderSearchResults = useCallback(
    ({
      providerSearchParams,
      setShowFacilities,
      setShowProviderGroups,
      setShowProviders,
      choosePCP = false,
      cleanUpProviderCalls,
      pseudoVerticalsDetails,
      selectedCategory,
      selectedCategoryPayload,
      specialtySearch,
      searchType,
      providerQueryTypes,
      providerType = 'All',
      setShowVirtualResultsOnly,
    }: GetProviderSearchResultsProps) => {
      // Set these variables so that only virtual care results are shown when applicable
      setShowVirtualResultsOnly(skipCalls);
      setShowProviders(!skipCalls);
      setShowFacilities(!skipCalls);
      setShowProviderGroups(!skipCalls);
      const canFetchData = selectedCategory || choosePCP;
      if (!canFetchData || skipCalls) {
        return;
      }
      setProviderSearchParams(providerSearchParams);
      const callProvidersResult = (params, code) => {
        const hasProviderTypeInProviderQueryTypes =
          providerQueryTypes?.length > 0
            ? providerQueryTypes.filter(
                (item) =>
                  ProviderTypes[
                    enableCategoryCountV2 ? item : item.providerType
                  ] === params.providerType
              ).length > 0
            : true;
        const enableProviderSearch =
          hasProviderTypeInProviderQueryTypes &&
          ['All', ProviderType.PRACTITIONER].includes(providerType);

        const enableProviderGroupSearch =
          ['All', ProviderType.MEDICAL_GROUP].includes(providerType) &&
          hasProviderTypeInProviderQueryTypes &&
          (!choosePCP || currentMember.lineOfBusiness === LOB.ENI);
        const enableFacilitySearch =
          ['All', ProviderType.ORGANIZATION].includes(providerType) &&
          hasProviderTypeInProviderQueryTypes &&
          !choosePCP;

        const providerTypeMapping = {
          P: enableProviderSearch
            ? () => {
                getProvidersResult(params);
              }
            : () => {
                setShowProviders(false);
              },
          M: enableProviderGroupSearch
            ? () => {
                getMedGroupResult(params);
              }
            : () => {
                setShowProviderGroups(false);
              },
          O: enableFacilitySearch
            ? () => {
                getOrgResult(params);
              }
            : () => {
                setShowFacilities(false);
              },
        };
        setCodes((prev) => [...prev, code]);
        providerTypeMapping[code]();
      };

      const { practitionerParams, orgParams, medGrpParams } =
        getProviderSearchParams(
          providerSearchParams,
          selectedCategoryPayload,
          specialtySearch ? Constants.SEARCH_TYPES.SPECIALTY : searchType,
          choosePCP,
          cleanUpProviderCalls,
          providerQueryTypes,
          enableCategoryCountV2,
          featureFlags
        );

      getDataForMixedResults({
        callProvidersResult,
        setShowFacilities,
        setShowProviderGroups,
        setShowProviders,
        medGrpParams,
        orgParams,
        practitionerParams,
        choosePCP,
        cleanUpProviderCalls,
        pseudoVerticalsDetails,
        searchType: providerSearchParams.searchType,
        selectedCategory,
        memberLob: currentMember.lineOfBusiness,
        selectedCategoryPayload,
      });
    },
    [
      getProvidersResult,
      getOrgResult,
      getMedGroupResult,
      suppressProviderGroupForPCP,
    ]
  );

  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [showStates] = useSessionStorage<{
    showProviders?: boolean;
    showFacilities?: boolean;
    showProviderGroups?: boolean;
  }>(Constants.STORAGE_KEYS.SESSION.SHOW_CATEGORIES, {});

  const hitProviderSearchTelemetry = async () => {
    const filteredResults = [
      ...(showStates?.showProviders
        ? data?.providers?.slice(
            0,
            Constants.RESULT_SECTION
              .MAX_PROVIDERS_TO_DISPLAY_IN_MIXED_RESULTS_SCREEN
          )
        : []),
      ...(showStates?.showFacilities
        ? data?.facilities?.slice(
            0,
            Constants.RESULT_SECTION
              .MAX_PROVIDERS_TO_DISPLAY_IN_MIXED_RESULTS_SCREEN
          )
        : []),
      ...(showStates?.showProviderGroups
        ? data?.medicalGroup?.slice(
            0,
            Constants.RESULT_SECTION
              .MAX_PROVIDERS_TO_DISPLAY_IN_MIXED_RESULTS_SCREEN
          )
        : []),
    ].map((item) => ({
      resultId: item?.providerId,
      resultType: getProviderType(item?.providerType),
      name: item?.providerName,
      specialty: item?.speciality,
      address: concatenateStrings(
        item?.address?.line?.join(''),
        item?.address?.city,
        item?.address?.district,
        item?.address?.state,
        item?.address?.postalCode
      ),
      coverageType: Array.isArray(providerSearchParams?.coverageType)
        ? providerSearchParams?.coverageType?.map(getTelemetryCoverageType)
        : getTelemetryCoverageType(providerSearchParams?.coverageType),
      rating: item?.healthGradeRating ? parseFloat(item?.healthGradeRating) : 0,
      numRatings: item?.surveyCount ?? 0,
      inNetworkLabel: item?.networkStatus,
      costIndicationLabel: item?.costIndicator,
    }));
    const telemetryVariables = {
      correlationId:
        providersResult?.headers?.correlationId ??
        organizationResult?.headers?.correlationId ??
        medGrpResult?.headers?.correlationId ??
        correlationIDSessionStorage,
      timestamp: new Date().toISOString(),
      searchQuery: providerSearchParams?.search ?? '',
      searchLocation: {
        locationInputField: name ?? '',
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        appliedSearchRadiusMiles: providerSearchParams?.searchRadius,
      },
      interpretedAsNameSearch:
        providerSearchParams?.searchType !== 'speciality',
      viewType: 'mixed',
      results: filteredResults,
      resultPage: 1,
      pageSize:
        Constants.RESULT_SECTION
          .MAX_PROVIDERS_TO_DISPLAY_IN_MIXED_RESULTS_SCREEN,
      resultedInNullSearch: filteredResults?.length === 0,
      event: 'mixed',
      searchPlatform: 'web',
    };
    await providerSearchTelemetry({
      variables: telemetryVariables,
    });
  };

  const hitErrorScenarioTelemetry = async () => {
    const telemetryVariables = {
      correlationId:
        providersResult?.headers?.correlationId ??
        organizationResult?.headers?.correlationId ??
        medGrpResult?.headers?.correlationId ??
        correlationIDSessionStorage,
      searchPlatform: 'web',
      timestamp: new Date().toISOString(),
      searchQuery: providerSearchParams?.search ?? '',
      searchLocation: {
        locationInputField: name ?? '',
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        appliedSearchRadiusMiles: providerSearchParams?.searchRadius,
      },
      interpretedAsNameSearch:
        providerSearchParams?.searchType !== 'speciality',
      viewType: 'mixed',
      results: [],
      resultPage: 1,
      pageSize:
        Constants.RESULT_SECTION
          .MAX_PROVIDERS_TO_DISPLAY_IN_MIXED_RESULTS_SCREEN,
      searchError: providerHasError?.status?.toString(),
      event: 'mixed',
    };
    await providerSearchTelemetry({
      variables: telemetryVariables,
    });
  };

  useEffect(() => {
    const urlPath = window?.location?.pathname?.split('/');
    if (
      completedCodes.length === codes.length &&
      JSON.stringify(completedCodes.sort()) === JSON.stringify(codes.sort()) &&
      completedCodes.length > 0 &&
      codes.length > 0 &&
      urlPath.length === 4 &&
      isSearchRelevancyEventTrackingEnabled
    ) {
      hitProviderSearchTelemetry();
    } else if (
      isSearchRelevancyEventTrackingEnabled &&
      providerHasError?.status
    ) {
      hitErrorScenarioTelemetry();
    }
  }, [completedCodes, codes, providerHasError?.status]);

  return {
    data,
    error,
    medGrpIsLoading,
    medGrpResult,
    providerIsLoading,
    medGrpData,
    providersResult,
    providerHasError,
    orgIsLoading,
    organizationResult,
    getProviderSearchResults,
  };
};
